/**
 * Independent check — V98-3-5 (ADR-0046 T11–T13; research §2 rules 11, 19).
 * Only the checker concludes. Agree or Exception, a note (stated minimum)
 * and at least one SUBSTANTIVE reviewed item, each linked to the version
 * looked at. An Exception disputes the approval: it is re-routed or revoked
 * by the next eligible approver and opens a finding.
 *
 * V98-3 review U11: the check names what it is about in plain words and
 * links to it ("Open what you're checking · v2"); the first reviewed item
 * is prefilled from the server's `about` (reference + version), never parsed
 * from a title; kinds are plain nouns. Errors clear as soon as the field
 * changes (U9).
 *
 * EscalationFollowUp (U1): an overdue check escalated to you names the
 * checker and when it escalated; acknowledge it, nudge the checker (leads),
 * "Reassign checker…" (a picker of who could take it over — each person who
 * can't says why — plus a reason) or "Extend (≤ 30 days)…" (a later date
 * within 30 days of the original self-approval, plus a reason). When it
 * can't be extended any more the reason is stated instead of a button.
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CheckFollowUpFacts } from '@aigovops/core';
import { Alert, Button, Field, Input, Radio, Select, Textarea } from '@aigovops/design-system';
import { acknowledgeEscalation, checkFollowUp, concludeCheck, extendCheck, reassignCheck } from '../../lib/coverage-api';
import { nudgeMessage, useNudges } from '../../lib/nudge-state';
import { ARTIFACT_KINDS, CHECK_MAX_DAYS, NOTE_MIN, checkerWhy, prefillArtifact } from '../../lib/item-model';
import { firstName, fmtDay, itemHref, nameOf, type Names } from '../../lib/today-model';
import { AskAdmin } from '../AskAdmin';
import { ActionDialog, Chip, errorText } from '../today/shared';

/** `label`: a prefilled item shown by its human title (R3-U21: never the raw record id); cleared to type another. */
interface Artifact { kind: string; id: string; version: string; label?: string }
export type CheckAbout = { kind: string; id: string; title: string; version: number | null } | null | undefined;

export function CheckerForm({ slug, prefix, checkId, about, onDone }: {
  slug: string; prefix: string; checkId: string; about: CheckAbout; onDone: (msg: string, focusId: string) => void;
}) {
  const qc = useQueryClient();
  const [conclusion, setConclusion] = useState<'agree' | 'exception' | ''>('');
  const [note, setNote] = useState('');
  const [arts, setArts] = useState<Artifact[]>(() => {
    const a = prefillArtifact(about);
    return [about && a.id ? { ...a, label: about.title } : a];
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const clear = (k: string) => { setError(null); if (errors[k]) setErrors((x) => ({ ...x, [k]: '' })); };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: Record<string, string> = {};
    if (!conclusion) errs.conclusion = 'Choose Agree or Exception.';
    if (note.trim().length < NOTE_MIN) errs.note = `Write at least ${NOTE_MIN} characters, even when you agree. ${note.trim().length}/${NOTE_MIN}.`;
    const filled = arts.filter((a) => a.id.trim() && a.version.trim());
    if (!filled.length) errs.arts = 'Add at least one thing you reviewed, with the version you looked at.';
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) return;
    setBusy(true); setError(null);
    try {
      await concludeCheck(slug, checkId, { conclusion: conclusion as 'agree' | 'exception', note: note.trim(), reviewed_artifacts: filled.map((a) => ({ kind: a.kind, id: a.id.trim(), version: a.version.trim() })) });
      await qc.invalidateQueries({ queryKey: ['today', slug] });
      const msg = conclusion === 'agree' ? 'Check signed · agree' : 'Check signed · exception · the approval is disputed and a finding opens';
      setDone(msg);
      onDone(msg, 'check-done');
    } catch (err) { setError(errorText(err, 'The check could not be signed.')); }
    finally { setBusy(false); }
  };

  if (done) return <div className="gp-state gp-state--ok" id="check-done" tabIndex={-1}><strong>{done}</strong></div>;
  const set = (i: number, k: keyof Artifact, v: string) => { setArts((x) => x.map((a, j) => (j === i ? { ...a, [k]: v } : a))); clear('arts'); };
  return (
    <form className="gp-form" onSubmit={submit} noValidate aria-labelledby="check-h">
      {about ? (
        <div className="gp-state" data-part="checking">
          <strong>{about.title}{about.version != null ? ` · v${about.version}` : ''}</strong>
          <span>
            <Link to={`${itemHref(prefix, { kind: about.kind, id: about.id })}#locked`}>{`Open what you’re checking${about.version != null ? ` · v${about.version}` : ''}`}</Link>
          </span>
        </div>
      ) : null}
      <h3 id="check-h">Your conclusion</h3>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <fieldset className="gp-fieldset" aria-describedby={errors.conclusion ? 'check-concl-err' : undefined} aria-invalid={errors.conclusion ? true : undefined}>
        <legend>Conclusion</legend>
        <Radio id="check-agree" name="conclusion" label="Agree" description="The approval was appropriate" checked={conclusion === 'agree'} onChange={() => { setConclusion('agree'); clear('conclusion'); }} />
        <Radio id="check-exception" name="conclusion" label="Exception" description="Disputes the approval: it is re-routed and a finding opens" checked={conclusion === 'exception'} onChange={() => { setConclusion('exception'); clear('conclusion'); }} />
        {errors.conclusion ? <p id="check-concl-err" role="alert" className="gp-field-err">{errors.conclusion}</p> : null}
      </fieldset>
      <fieldset className="gp-fieldset" aria-describedby={errors.arts ? 'check-arts-err' : 'check-arts-hint'} aria-invalid={errors.arts ? true : undefined}>
        <legend>What you reviewed</legend>
        <p id="check-arts-hint" className="gp-dim">At least one substantive item · the approval record alone doesn’t count.</p>
        {arts.map((a, i) => (a.label ? (
          <div className="gp-ow-line" key={i} data-part="prefilled-item">
            <span>{`Item ${i + 1} · ${ARTIFACT_KINDS.find((k) => k.id === a.kind)?.label ?? 'Item'}:`}</span>
            <strong>{`${a.label}${a.version ? ` · ${a.version}` : ''}`}</strong>
            <Button compact variant="ghost" aria-label={`Use another reference · item ${i + 1}`}
              onClick={() => { setArts((x) => x.map((y, j) => (j === i ? { kind: 'evidence', id: '', version: '' } : y))); window.setTimeout(() => document.getElementById(`art-id-${i}`)?.focus(), 0); }}>
              Use another reference
            </Button>
          </div>
        ) : (
          <div className="gp-form-row" key={i}>
            <Field id={`art-kind-${i}`} label={`Item ${i + 1} · what`}>
              <Select value={a.kind} onChange={(e) => set(i, 'kind', e.currentTarget.value)}>
                {ARTIFACT_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </Select>
            </Field>
            <Field id={`art-id-${i}`} label={`Item ${i + 1} · reference`}>
              <Input value={a.id} maxLength={200} onChange={(e) => set(i, 'id', e.currentTarget.value)} />
            </Field>
            <Field id={`art-ver-${i}`} label={`Item ${i + 1} · version`} hint="The version you looked at">
              <Input value={a.version} maxLength={200} onChange={(e) => set(i, 'version', e.currentTarget.value)} />
            </Field>
          </div>
        )))}
        {errors.arts ? <p id="check-arts-err" role="alert" className="gp-field-err">{errors.arts}</p> : null}
        <div className="gp-actions"><Button compact onClick={() => setArts((x) => [...x, { kind: 'evidence', id: '', version: '' }])}>Add another item</Button></div>
      </fieldset>
      <Field id="check-note" label="Note" required hint={`At least ${NOTE_MIN} characters, even when you agree`} error={errors.note}>
        <Textarea rows={3} maxLength={4000} value={note} onChange={(e) => { setNote(e.currentTarget.value); clear('note'); }} />
      </Field>
      <div className="gp-actions"><Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Signing…' : 'Sign the check'}</Button></div>
    </form>
  );
}

const addDay = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

export function EscalationFollowUp({ slug, prefix, checkId, itemKey, title, escalation, names, todayDay, about, canNudge, onAddParty, onDone }: {
  slug: string; prefix: string; checkId: string; itemKey: string; title: string;
  /** U1: the checker (id + name), when it escalated and how far it may still move — from the Today row. */
  escalation: CheckFollowUpFacts | undefined;
  names: Names;
  todayDay: string;
  about: CheckAbout;
  /** Nudging is a lead / admin action (server-enforced). */
  canNudge: boolean;
  /** Owner/admin: seat an independent party inline when nobody here could take the check over. */
  onAddParty?: (el: HTMLElement) => void;
  onDone: (msg: string, focusId: string) => void;
}) {
  const qc = useQueryClient();
  const checkerId = escalation?.checker_user_id ?? null;
  const checkerName = escalation?.checker_name ?? (checkerId ? nameOf(names, checkerId) : 'The checker');
  const [error, setError] = useState<string | null>(null);
  const [acked, setAcked] = useState(false);
  const [moved, setMoved] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'reassign' | 'extend' | null>(null);
  const [who, setWho] = useState('');
  const [due, setDue] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dlgError, setDlgError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fu = useQuery({ queryKey: ['check-follow-up', slug, checkId], queryFn: () => checkFollowUp(slug, checkId), retry: false, staleTime: 30_000 });
  const opts = fu.data?.follow_up;
  const nudges = useNudges(slug);
  const sent = nudges.state[itemKey];
  const latestDay = (opts?.latest_due_at ?? escalation?.latest_due_at ?? '').slice(0, 10);
  const minDay = addDay(todayDay, 1);
  const ack = async () => {
    try { await acknowledgeEscalation(slug, checkId); setAcked(true); onDone('Escalation acknowledged · you own the follow-up', 'esc-state'); }
    catch (e) { setError(errorText(e, 'Could not acknowledge.')); }
  };
  const nudge = async () => {
    const s = await nudges.send(itemKey, { user_id: checkerId, name: checkerName });
    onDone(nudgeMessage(s, checkerName), 'esc-nudge');
  };
  const open = (d: 'reassign' | 'extend') => { setDialog(d); setErrors({}); setDlgError(null); setReason(''); setWho(''); setDue(''); };
  const clear = (k: string) => { setDlgError(null); if (errors[k]) setErrors((x) => ({ ...x, [k]: '' })); };
  const submit = async () => {
    const errs: Record<string, string> = {};
    if (dialog === 'reassign' && !who) errs.who = 'Choose who checks it instead.';
    if (dialog === 'extend' && !due) errs.due = 'Choose the new check-by date.';
    if (due && (due < minDay || (latestDay && due > latestDay))) errs.due = `Pick a date from ${fmtDay(minDay)} to ${fmtDay(latestDay)} (${CHECK_MAX_DAYS} days after the self-approval).`;
    if (reason.trim().length < 3) errs.reason = 'Give a reason (required).';
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) return false;
    setBusy(true); setDlgError(null);
    try {
      const r = dialog === 'reassign'
        ? await reassignCheck(slug, checkId, { checker_user_id: who, reason: reason.trim(), ...(due ? { due_at: due } : {}) })
        : await extendCheck(slug, checkId, { due_at: due, reason: reason.trim() });
      const msg = dialog === 'reassign'
        ? `Checker reassigned · ${nameOf(names, who)} checks it by ${fmtDay(r.check.due_at)}`
        : `Check extended · ${firstName(checkerName)} checks it by ${fmtDay(r.check.due_at)}`;
      setDialog(null); setMoved(msg);
      await qc.invalidateQueries({ queryKey: ['today', slug] });
      onDone(msg, 'esc-state');
      return true;
    } catch (e) { setDlgError(errorText(e, dialog === 'reassign' ? 'The checker could not be reassigned.' : 'The check could not be extended.')); return false; }
    finally { setBusy(false); }
  };
  const meta = escalation ? [
    `was due ${fmtDay(escalation.due_at)}`,
    escalation.escalated_at ? `escalated ${fmtDay(escalation.escalated_at)}` : null,
    escalation.extensions_used ? `extended ${escalation.extensions_used}×` : null,
  ].filter(Boolean).join(' · ') : null;
  const checkers = opts?.checkers;
  const none = checkers?.status === 'needs_independent_party';
  return (
    <div className="gp-form">
      <div className="gp-state gp-state--warn" id="esc-state" tabIndex={-1}>
        <strong>{moved ?? (acked ? 'Acknowledged · you own the follow-up' : `${checkerName} hasn’t concluded and the check is overdue`)}</strong>
        {meta && !moved ? <span className="gp-dim">{meta}</span> : null}
        {about ? <span><Link to={itemHref(prefix, { kind: about.kind, id: about.id })}>{`Open ${about.title}${about.version != null ? ` · v${about.version}` : ''}`}</Link></span> : null}
      </div>
      {error ? <Alert variant="error">{error}</Alert> : null}
      {moved ? null : (
        <div className="gp-actions">
          {acked ? null : <Button variant="primary" onClick={() => void ack()}>Acknowledge</Button>}
          {canNudge
            ? (sent ? <Chip id="esc-nudge" text={sent.kind === 'sent' ? `Nudged · ${sent.to}` : sent.kind === 'already' ? 'Nudged today' : sent.kind === 'away' ? `${checkerName} is away` : sent.text} tone="lock" />
              : <Button id="esc-nudge" onClick={() => void nudge()} aria-label={`${checkerId ? `Nudge ${checkerName}` : 'Nudge the checker'} · ${title}`}>{checkerId ? `Nudge ${firstName(checkerName)}` : 'Nudge the checker'}</Button>)
            : null}
          {opts ? <Button id="esc-reassign" onClick={() => open('reassign')}>Reassign checker…</Button> : null}
          {opts?.can_extend ? <Button id="esc-extend" onClick={() => open('extend')}>{`Extend (≤ ${CHECK_MAX_DAYS} days)…`}</Button> : null}
        </div>
      )}
      {!moved && opts && !opts.can_extend && opts.extend_blocked ? <p className="gp-dim" data-part="extend-blocked">{opts.extend_blocked}</p> : null}
      {!moved && fu.error ? (
        <>
          <p className="gp-dim">{errorText(fu.error, 'You can’t move this check.')}</p>
          <AskAdmin slug={slug} compact verb="reassign the checker or extend the date" what="manages independent checks"
            subject={`GAC Pro: overdue independent check · ${title} — reassign the checker or extend (≤ ${CHECK_MAX_DAYS} days)`} />
        </>
      ) : null}
      <ActionDialog open={dialog !== null} title={dialog === 'extend' ? 'Extend the check-by date' : 'Reassign the checker'}
        submitLabel={dialog === 'extend' ? 'Extend' : 'Reassign'} busy={busy} error={dlgError}
        submitDisabled={dialog === 'reassign' && none}
        onClose={() => { setDialog(null); setDlgError(null); }} onSubmit={submit}>
        {dialog === 'reassign' && checkers ? (
          checkers.status === 'needs_independent_party' ? (
            <div data-part="needs-party">
              <p>{checkers.prompt}</p>
              <ul className="gp-plain-list" aria-label="Why nobody here is eligible">
                {checkers.checkers.map((c) => <li key={c.user_id}>{`${nameOf(names, c.user_id)} · ${checkerWhy(c.why, 'other')}`}</li>)}
              </ul>
              {onAddParty
                ? <Button id="esc-add-party" onClick={(e) => onAddParty(e.currentTarget)}>Add an independent party…</Button>
                : <AskAdmin slug={slug} compact verb="add an independent party" what="manages coverage" subject={`GAC Pro: add an independent party to check · ${title}`} />}
            </div>
          ) : (
            <Field id="esc-who" label="New checker" required hint="Outside the self-approver’s reporting line and not in a conflicting pair" error={errors.who}>
              <Select value={who} onChange={(e) => { setWho(e.currentTarget.value); clear('who'); }}>
                <option value="">Choose…</option>
                {checkers.checkers.map((c) => (
                  <option key={c.user_id} value={c.user_id} disabled={!c.eligible}>
                    {`${nameOf(names, c.user_id)}${c.eligible ? '' : ` · not eligible · ${checkerWhy(c.why, 'other')}`}`}
                  </option>
                ))}
              </Select>
            </Field>
          )
        ) : null}
        {dialog === 'reassign' && none ? null : (
          <>
            <Field id="esc-due" label={dialog === 'extend' ? 'New check-by date' : 'Check by'} required={dialog === 'extend'}
              hint={dialog === 'extend'
                ? `Later than ${fmtDay(escalation?.due_at ?? opts?.due_at ?? todayDay)} · by ${fmtDay(latestDay)} at the latest`
                : `Optional · default ${fmtDay(opts?.default_due_at ?? latestDay)} · by ${fmtDay(latestDay)} at the latest`}
              error={errors.due}>
              <Input type="date" min={minDay} max={latestDay || undefined} value={due} onChange={(e) => { setDue(e.currentTarget.value); clear('due'); }} />
            </Field>
            <Field id="esc-reason" label="Reason" required hint="Required · recorded on the check’s history" error={errors.reason}>
              <Textarea rows={3} maxLength={2000} value={reason} onChange={(e) => { setReason(e.currentTarget.value); clear('reason'); }} />
            </Field>
          </>
        )}
      </ActionDialog>
    </div>
  );
}
